import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabase';
import { resolveIllustrationSource } from '../../../data/plumbingIllustrations';
import StepActions from './StepActions';

const EquipmentStep = ({ data, onUpdate, onNext, onBack, isActive = true, showBack = true }) => {
  const selectedCategory = data?.category || null;
  const [selectedEquipment, setSelectedEquipment] = useState(data?.equipment || null);
  const [equipments, setEquipments] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSelectedEquipment(data?.equipment || null);
  }, [data?.category?.id, data?.equipment?.id]);

  useEffect(() => {
    const fetchEquipments = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!selectedCategory?.id) {
          setEquipments([]);
          setSelectedEquipment(null);
          setLoading(false);
          return;
        }

        const { data: equipmentsData, error: equipmentsError } = await supabase
          .from('equipment')
          .select('id, name, description, category')
          .eq('actif', true)
          .eq('category', selectedCategory.id)
          .order('name');

        if (equipmentsError) throw equipmentsError;

        setEquipments(equipmentsData || []);

        if (equipmentsData?.length === 1) {
          setSelectedEquipment(equipmentsData[0]);
        } else if (data?.equipment && data.equipment.category !== selectedCategory.id) {
          setSelectedEquipment(null);
        }
      } catch (err) {
        console.error('Error fetching equipments:', err);
        setError(err.message || 'Erreur lors du chargement des équipements');
      } finally {
        setLoading(false);
      }
    };

    fetchEquipments();
  }, [selectedCategory?.id]);

  const handleEquipmentSelect = (equipment) => {
    setSelectedEquipment(equipment);
    setErrors({});
    handleSubmit(equipment);
  };

  const handleSubmit = (equipmentParam) => {
    const equipmentToSubmit = equipmentParam || selectedEquipment;

    if (!equipmentToSubmit) {
      setErrors({ equipment: 'Veuillez sélectionner un équipement' });
      return;
    }

    onUpdate({
      category: selectedCategory,
      equipment: equipmentToSubmit,
    });

    onNext(true);
  };
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Chargement des équipements...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
              <p className="text-error font-medium mb-4">{error}</p>
              <Button onClick={fetchEquipments} variant="outline">
                Réessayer
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedCategory) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} color="var(--color-warning)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">Veuillez d'abord sélectionner une catégorie.</p>
              <StepActions isActive={isActive} showBack={showBack}>
                <Button onClick={onBack} variant="outline" className="mt-4">
                  Retour
                </Button>
              </StepActions>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-card rounded-lg shadow-md p-0 sm:p-6 lg:p-8">
        <div className="flex items-start gap-4 px-1 sm:px-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">
              Quel équipement est concerné ?
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              Quel élément pose problème ?
            </p>
          </div>
        </div>

        <div className="flex justify-between pt-4 sm:pt-6 border-t border-border" />

        <div className="space-y-6 sm:space-y-8 mt-4 sm:mt-0">
          <div>
            {equipments.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 items-stretch">
                {equipments.map((equipment) => (
                  <button
                    key={equipment.id}
                    type="button"
                    onClick={() => handleEquipmentSelect(equipment)}
                    className={`h-full min-h-[4.75rem] sm:min-h-[5rem] p-2.5 sm:p-4 rounded-lg border-2 transition-all duration-200 flex items-center gap-2 sm:gap-3 text-left ${
                      selectedEquipment?.id === equipment.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 bg-card'
                    }`}
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 flex items-center justify-center">
                      <Image
                        src={resolveIllustrationSource(selectedCategory?.icon, equipment.name)}
                        alt=""
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <span className="font-medium text-xs sm:text-sm flex-1 leading-snug line-clamp-2">
                      {equipment.name}
                    </span>
                    {selectedEquipment?.id === equipment.id && (
                      <Icon
                        name="CheckCircle"
                        size={18}
                        color="var(--color-primary)"
                        className="flex-shrink-0"
                      />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Aucun équipement disponible pour cette catégorie.</p>
              </div>
            )}
            {errors?.equipment && (
              <div className="mt-4 flex items-center gap-2 text-sm text-error">
                <Icon name="AlertCircle" size={16} />
                <span>{errors?.equipment}</span>
              </div>
            )}
          </div>
        </div>

        <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-6 sm:pt-8 mt-6 sm:mt-8 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
        </StepActions>
      </div>
    </div>
  );
};

export default EquipmentStep;